import { useEffect, useRef, useState, useCallback } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";
import { useSearchStore } from "../../stores/searchStore";
import { useThemeStore } from "../../stores/themeStore";
import { useAuthStore } from "../../stores/authStore";
import { queryOperatorsProtected } from "../../lib/operatorsApi";
import type { OperatorWithCoords } from "../../types/database";
import type { MapFeature, ClusterFeature } from "../../hooks/useSupercluster";
import { isCluster } from "../../hooks/useSupercluster";
import { cn } from "../../lib/utils";
import { getTypologyConfig, getCategoryConfig } from "../../lib/categoryConfig";
import { Database, Trash2, MapPin, Globe } from "lucide-react";
import { useTileCache } from "../../hooks/useTileCache";
import { useOperatorsCache } from "../../hooks/useOperatorsCache";
import { useDmoArea, isDmoMacroCategory } from "../../hooks/useDmoArea";

// Debounce helper for map movements
function debounce<T extends (...args: Parameters<T>) => void>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

// Fix Leaflet default marker icon issue
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";
import iconRetina from "leaflet/dist/images/marker-icon-2x.png";

const DefaultIcon = L.icon({
  iconUrl: icon,
  iconRetinaUrl: iconRetina,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

// Disable leaflet-draw tooltips
if (L.drawLocal) {
  L.drawLocal.draw.handlers.polygon.tooltip = {
    start: "",
    cont: "",
    end: "",
  };
  L.drawLocal.draw.handlers.polyline.tooltip = {
    start: "",
    cont: "",
    end: "",
  };
  L.drawLocal.draw.handlers.rectangle.tooltip = {
    start: "",
  };
  L.drawLocal.draw.handlers.circle.tooltip = {
    start: "",
  };
  L.drawLocal.draw.handlers.simpleshape.tooltip = {
    end: "",
  };
  L.drawLocal.edit.handlers.edit.tooltip = {
    text: "",
    subtext: "",
  };
}

// Patch leaflet-draw circle editing to avoid ReferenceError on radius
// The upstream build assigns to an undeclared `radius` inside _resize,
// which throws in strict mode (ESM bundlers). We redefine the method
// with a locally scoped variable.
const circleEditProto = (L as any).Edit?.Circle?.prototype as
  | ({ _resizePatched?: boolean } & Record<string, any>)
  | undefined;

if (circleEditProto && !circleEditProto._resizePatched) {
  circleEditProto._resizePatched = true;
  circleEditProto._resize = function _resize(this: any, latlng: L.LatLng) {
    const center = this._moveMarker.getLatLng();
    const radius = L.GeometryUtil.isVersion07x()
      ? center.distanceTo(latlng)
      : this._map.distance(center, latlng);

    this._shape.setRadius(radius);

    if (this._map.editTooltip && this._map._editTooltip) {
      this._map._editTooltip.updateContent({
        text: `${L.drawLocal.edit.handlers.edit.tooltip.subtext}<br />${L.drawLocal.edit.handlers.edit.tooltip.text}`,
        subtext: `${L.drawLocal.draw.handlers.circle.radius}: ${L.GeometryUtil.readableDistance(
          radius,
          true,
          this.options.feet,
          this.options.nautic
        )}`,
      });
    }

    this._map.fire(L.Draw.Event.EDITRESIZE, { layer: this._shape });
  };
}

interface MapViewProps {
  clusters: MapFeature[];
  isLoading?: boolean;
  onOperatorClick?: (operator: OperatorWithCoords) => void;
  onClusterClick?: (cluster: ClusterFeature, expansionZoom: number) => void;
  getClusterExpansionZoom: (clusterId: number) => number;
  getClusterLeaves?: (clusterId: number, limit?: number) => OperatorWithCoords[];
  filteredOperators?: OperatorWithCoords[];
  className?: string;
}

// Get color for typology using the shared config
// Uses macro category palette if available
const getMarkerColor = (typology: string | null | undefined, macroCategory?: string | null | undefined): string => {
  return getTypologyConfig(typology, macroCategory).color;
};

// SVG paths for Lucide icons used in popups
const iconSvgMap: Record<string, string> = {
  Hotel: '<path d="M18 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2Z"/><path d="m9 16 .348-.24c1.465-1.013 3.84-1.013 5.304 0L15 16"/><path d="M8 7h.01"/><path d="M16 7h.01"/><path d="M12 7h.01"/><path d="M12 11h.01"/><path d="M16 11h.01"/><path d="M8 11h.01"/><path d="M10 22v-6.5m4 0V22"/>',
  BedDouble: '<path d="M2 20v-8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8"/><path d="M4 10V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v4"/><path d="M18 10H6"/><path d="M2 18h20"/>',
  Building2: '<path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/><path d="M10 6h4"/><path d="M10 10h4"/><path d="M10 14h4"/><path d="M10 18h4"/>',
  Landmark: '<line x1="3" x2="21" y1="22" y2="22"/><line x1="6" x2="6" y1="18" y2="11"/><line x1="10" x2="10" y1="18" y2="11"/><line x1="14" x2="14" y1="18" y2="11"/><line x1="18" x2="18" y1="18" y2="11"/><polygon points="12 2 20 7 4 7"/>',
  Flag: '<path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" x2="4" y1="22" y2="15"/>',
  Waves: '<path d="M2 6c.6.5 1.2 1 2.5 1C7 7 7 5 9.5 5c2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/><path d="M2 12c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/><path d="M2 18c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/>',
  Anchor: '<circle cx="12" cy="5" r="3"/><line x1="12" x2="12" y1="22" y2="8"/><path d="M5 12H2a10 10 0 0 0 20 0h-3"/>',
  Utensils: '<path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"/><path d="M7 2v20"/><path d="M21 15V2v0a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3zm0 0v7"/>',
  Coffee: '<path d="M10 2v2"/><path d="M14 2v2"/><path d="M16 8a1 1 0 0 1 1 1v8a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V9a1 1 0 0 1 1-1h14a4 4 0 1 1 0 8h-1"/><path d="M6 2v2"/>',
  Wine: '<path d="M8 22h8"/><path d="M7 10h10"/><path d="M12 15v7"/><path d="M12 15a5 5 0 0 0 5-5c0-2-.5-4-2-8H9c-1.5 4-2 6-2 8a5 5 0 0 0 5 5Z"/>',
  CakeSlice: '<circle cx="9" cy="7" r="2"/><path d="M7.2 7.9 3 11v9c0 .6.4 1 1 1h16c.6 0 1-.4 1-1v-9c0-2-3-6-7-8l-3.6 2.6"/><path d="M16 13H3"/><path d="M16 17H3"/>',
  ChefHat: '<path d="M17 21a1 1 0 0 0 1-1v-5.35c0-.457.316-.844.727-1.041a4 4 0 0 0-2.134-7.589 5 5 0 0 0-9.186 0 4 4 0 0 0-2.134 7.588c.411.198.727.585.727 1.041V20a1 1 0 0 0 1 1Z"/><path d="M6 17h12"/>',
  Trophy: '<path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>',
  Shirt: '<path d="M20.38 3.46 16 2a4 4 0 0 1-8 0L3.62 3.46a2 2 0 0 0-1.34 2.23l.58 3.47a1 1 0 0 0 .99.84H6v10c0 1.1.9 2 2 2h8a2 2 0 0 0 2-2V10h2.15a1 1 0 0 0 .99-.84l.58-3.47a2 2 0 0 0-1.34-2.23Z"/>',
  Gem: '<path d="M6 3h12l4 6-10 13L2 9Z"/><path d="M11 3 8 9l4 13 4-13-3-6"/><path d="M2 9h20"/>',
  Factory: '<path d="M2 20a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8l-7 5V8l-7 5V4a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"/><path d="M17 18h1"/><path d="M12 18h1"/><path d="M7 18h1"/>',
  Wrench: '<path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>',
  Cpu: '<rect width="16" height="16" x="4" y="4" rx="2"/><rect width="6" height="6" x="9" y="9" rx="1"/><path d="M15 2v2"/><path d="M15 20v2"/><path d="M2 15h2"/><path d="M2 9h2"/><path d="M20 15h2"/><path d="M20 9h2"/><path d="M9 2v2"/><path d="M9 20v2"/>',
  Wheat: '<path d="M2 22 16 8"/><path d="M3.47 12.53 5 11l1.53 1.53a3.5 3.5 0 0 1 0 4.94L5 19l-1.53-1.53a3.5 3.5 0 0 1 0-4.94Z"/><path d="M7.47 8.53 9 7l1.53 1.53a3.5 3.5 0 0 1 0 4.94L9 15l-1.53-1.53a3.5 3.5 0 0 1 0-4.94Z"/><path d="M11.47 4.53 13 3l1.53 1.53a3.5 3.5 0 0 1 0 4.94L13 11l-1.53-1.53a3.5 3.5 0 0 1 0-4.94Z"/><path d="M20 2h2v2a4 4 0 0 1-4 4h-2V6a4 4 0 0 1 4-4Z"/><path d="M11.47 17.47 13 19l-1.53 1.53a3.5 3.5 0 0 1-4.94 0L5 19l1.53-1.53a3.5 3.5 0 0 1 4.94 0Z"/><path d="M15.47 13.47 17 15l-1.53 1.53a3.5 3.5 0 0 1-4.94 0L9 15l1.53-1.53a3.5 3.5 0 0 1 4.94 0Z"/><path d="M19.47 9.47 21 11l-1.53 1.53a3.5 3.5 0 0 1-4.94 0L13 11l1.53-1.53a3.5 3.5 0 0 1 4.94 0Z"/>',
  TreePine: '<path d="m17 14 3 3.3a1 1 0 0 1-.7 1.7H4.7a1 1 0 0 1-.7-1.7L7 14h-.3a1 1 0 0 1-.7-1.7L9 9h-.2A1 1 0 0 1 8 7.3L12 3l4 4.3a1 1 0 0 1-.8 1.7H15l3 3.3a1 1 0 0 1-.7 1.7H17Z"/><path d="M12 22v-3"/>',
  Sofa: '<path d="M20 9V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v3"/><path d="M2 11v5a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-5a2 2 0 0 0-4 0v2H6v-2a2 2 0 0 0-4 0Z"/><path d="M4 18v2"/><path d="M20 18v2"/><path d="M12 4v9"/>',
  Palette: '<circle cx="13.5" cy="6.5" r=".5" fill="currentColor"/><circle cx="17.5" cy="10.5" r=".5" fill="currentColor"/><circle cx="8.5" cy="7.5" r=".5" fill="currentColor"/><circle cx="6.5" cy="12.5" r=".5" fill="currentColor"/><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.555C21.965 6.012 17.461 2 12 2z"/>',
  Lamp: '<path d="M8 2h8l4 10H4L8 2Z"/><path d="M12 12v6"/><path d="M8 22v-2c0-1.1.9-2 2-2h4a2 2 0 0 1 2 2v2H8Z"/>',
  Briefcase: '<path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/><rect width="20" height="14" x="2" y="6" rx="2"/>',
  GraduationCap: '<path d="M21.42 10.922a1 1 0 0 0-.019-1.838L12.83 5.18a2 2 0 0 0-1.66 0L2.6 9.08a1 1 0 0 0 0 1.832l8.57 3.908a2 2 0 0 0 1.66 0z"/><path d="M22 10v6"/><path d="M6 12.5V16a6 3 0 0 0 12 0v-3.5"/>',
  Truck: '<path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/>',
  Plane: '<path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z"/>',
  Car: '<path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/><circle cx="7" cy="17" r="2"/><path d="M9 17h6"/><circle cx="17" cy="17" r="2"/>',
  Sparkles: '<path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/><path d="M5 3v4"/><path d="M19 17v4"/><path d="M3 5h4"/><path d="M17 19h4"/>',
  Heart: '<path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>',
  Theater: '<path d="M2 10s3-3 10-3 10 3 10 3"/><path d="M2 14s3 3 10 3 10-3 10-3"/><path d="M2 10v4"/><path d="M22 10v4"/><path d="M8 10v4"/><path d="M16 10v4"/><path d="M12 10v4"/>',
  Ticket: '<path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"/><path d="M13 5v2"/><path d="M13 17v2"/><path d="M13 11v2"/>',
  Store: '<path d="m2 7 4.41-4.41A2 2 0 0 1 7.83 2h8.34a2 2 0 0 1 1.42.59L22 7"/><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><path d="M15 22v-4a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4"/><path d="M2 7h20"/><path d="M22 7v3a2 2 0 0 1-2 2v0a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 16 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 12 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 8 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 4 12v0a2 2 0 0 1-2-2V7"/>',
  Home: '<path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>',
};

// Get SVG string for a typology's icon
const getTypologyIconSvg = (typology: string | null | undefined): string => {
  const config = getTypologyConfig(typology);
  const iconName = config.icon.displayName || 'Store';
  const svgPath = iconSvgMap[iconName] || iconSvgMap.Store;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${svgPath}</svg>`;
};

// Tile layer URLs
const TILE_LAYERS = {
  // OpenStreetMap standard - mostra nomi locali italiani quando disponibili
  // Corretto: subdomains 'abc' invece di 'abcd'
  light: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
  // CartoDB Dark Matter - dark theme (OSM non ha versione dark)
  dark: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
};

// Cluster size config
const getClusterSize = (count: number): { size: number; sizeClass: string } => {
  if (count >= 1000) {
    return { size: 54, sizeClass: "cluster-xlarge" };
  } else if (count >= 100) {
    return { size: 46, sizeClass: "cluster-large" };
  } else if (count >= 10) {
    return { size: 38, sizeClass: "cluster-medium" };
  }
  return { size: 32, sizeClass: "cluster-small" };
};

// Calculate category or typology percentages from operators
// If all operators have the same macro category, calculate typology percentages instead
function calculateCategoryPercentages(operators: OperatorWithCoords[]): Array<{ category: string; percentage: number; color: string }> {
  if (operators.length === 0) return [];

  // Check if all operators have the same macro category
  const firstCategory = operators[0]?.macro_category;
  const allSameCategory = operators.every(op => op.macro_category === firstCategory);

  const counts: Record<string, number> = {};

  if (allSameCategory && firstCategory) {
    // If all same category, calculate typology percentages
    operators.forEach((op) => {
      const typology = op.typology || "Non specificato";
      counts[typology] = (counts[typology] || 0) + 1;
    });
  } else {
    // Otherwise, calculate category percentages
    operators.forEach((op) => {
      const category = op.macro_category || "Non specificato";
      counts[category] = (counts[category] || 0) + 1;
    });
  }

  const total = operators.length;
  const percentages = Object.entries(counts)
    .map(([key, count]) => {
      const percentage = (count / total) * 100;
      let color: string;

      if (allSameCategory && firstCategory) {
        // Use typology color (with macro category context)
        color = getTypologyConfig(key, firstCategory).color;
      } else {
        // Use category color
        color = getCategoryConfig(key).color;
      }

      return {
        category: key,
        percentage,
        color,
      };
    })
    .sort((a, b) => b.percentage - a.percentage) // Sort by percentage descending
    .slice(0, 6); // Limit to top 6 for readability

  return percentages;
}

// Generate CSS conic-gradient for pie chart
function generatePieChartCSS(percentages: Array<{ percentage: number; color: string }>): string {
  if (percentages.length === 0) {
    return "background: #64748b";
  }

  if (percentages.length === 1) {
    return `background: ${percentages[0].color}`;
  }

  // Build conic-gradient: each segment starts at cumulative percentage
  let cumulative = 0;
  const stops: string[] = [];

  percentages.forEach(({ percentage, color }) => {
    const startPercent = cumulative;
    const endPercent = cumulative + percentage;
    stops.push(`${color} ${startPercent}% ${endPercent}%`);
    cumulative = endPercent;
  });

  // Fill remaining space with last color if needed
  if (cumulative < 100) {
    const lastColor = percentages[percentages.length - 1].color;
    stops.push(`${lastColor} ${cumulative}% 100%`);
  }

  return `background: conic-gradient(${stops.join(", ")})`;
}

// Get cluster color - use dominant typology color if available, otherwise pie chart, otherwise macro category color
const getClusterColor = (
  uniformTypology: string | null | undefined,
  uniformMacroCategory: string | null | undefined,
  sizeClass: string,
  pieChartCSS?: string
): string => {
  // Priority 1: If all operators have the same typology, use typology color
  if (uniformTypology) {
    const color = getTypologyConfig(uniformTypology, uniformMacroCategory || undefined).color;
    return `background: linear-gradient(145deg, ${color}, ${adjustColor(color, -20)})`;
  }

  // Priority 2: Use pie chart if available (shows distribution of categories/typologies)
  if (pieChartCSS) {
    return pieChartCSS;
  }

  // Priority 3: If all operators have the same macro category (but different typologies), use category color
  if (uniformMacroCategory) {
    const config = getCategoryConfig(uniformMacroCategory);
    const color = config.color;
    return `background: linear-gradient(145deg, ${color}, ${adjustColor(color, -20)})`;
  }

  // Priority 4: Fallback - neutral gray (should rarely happen if pie chart calculation works)
  const defaultColors: Record<string, string> = {
    "cluster-small": "background: linear-gradient(145deg, #64748b, #475569)",
    "cluster-medium": "background: linear-gradient(145deg, #64748b, #475569)",
    "cluster-large": "background: linear-gradient(145deg, #64748b, #475569)",
    "cluster-xlarge": "background: linear-gradient(145deg, #64748b, #475569)",
  };
  return defaultColors[sizeClass] || defaultColors["cluster-small"];
};

// Darken/lighten a hex color
function adjustColor(hex: string, amount: number): string {
  const num = parseInt(hex.replace("#", ""), 16);
  const r = Math.min(255, Math.max(0, (num >> 16) + amount));
  const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00ff) + amount));
  const b = Math.min(255, Math.max(0, (num & 0x0000ff) + amount));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

// Format count for display
const formatCount = (count: number): string => {
  if (count >= 10000) return `${(count / 1000).toFixed(0)}k`;
  if (count >= 1000) return `${(count / 1000).toFixed(1)}k`;
  return count.toLocaleString();
};

// Generate stable key for a feature based on its coordinates
const getFeatureKey = (feature: MapFeature): string => {
  const [lng, lat] = feature.geometry.coordinates;
  // Round to 4 decimal places for stability
  const roundedLat = Math.round(lat * 10000);
  const roundedLng = Math.round(lng * 10000);

  if (isCluster(feature)) {
    return `c_${roundedLat}_${roundedLng}_${feature.properties.point_count}`;
  } else {
    return `o_${(feature.properties as OperatorWithCoords).id}`;
  }
};

export function MapView({
  clusters,
  isLoading = false,
  onOperatorClick,
  onClusterClick,
  getClusterExpansionZoom,
  getClusterLeaves,
  filteredOperators = [],
  className
}: MapViewProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const markersLayer = useRef<L.LayerGroup | null>(null);
  const drawnItems = useRef<L.FeatureGroup | null>(null);
  const tileLayer = useRef<L.TileLayer | null>(null);
  const currentTileUrl = useRef<string | null>(null);
  const zoneBoundaryLayerRef = useRef<L.GeoJSON | null>(null);
  const dmoAreaLayerRef = useRef<L.GeoJSON | null>(null);

  // Track markers by their stable key
  const markersMap = useRef<Map<string, L.CircleMarker | L.Marker>>(new Map());
  const previousSelectedId = useRef<string | null>(null);
  const isFirstRender = useRef(true);
  const flyingGhostMarker = useRef<L.CircleMarker | null>(null);
  const circleUpdateIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isUpdatingPolygonRef = useRef(false);
  const isDraggingRef = useRef(false);

  const {
    setMapBounds,
    setZoomLevel,
    zoomLevel,
    drawnPolygon,
    setDrawnPolygon,
    zoneBoundary,
    isDrawingMode,
    setIsDrawingMode,
    isEditingMode,
    setIsEditingMode,
    drawingShapeType,
    selectedOperator,
    setSelectedOperator,
    hoveredOperatorId,
    setHoveredOperatorId,
    results,
    filters,
  } = useSearchStore();

  const { theme, resolvedTheme } = useThemeStore();
  const { token } = useAuthStore();
  const { isRegistered: isTileCacheRegistered, cacheStats: tileCacheStats, clearCache: clearTileCache, refreshStats: refreshTileStats } = useTileCache();
  const { cacheStats: operatorsCacheStats, clearCache: clearOperatorsCache, refreshStats: refreshOperatorsStats, isClearing: isClearingOperators } = useOperatorsCache();

  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [localZoom, setLocalZoom] = useState(6);
  const [isCacheInfoVisible, setIsCacheInfoVisible] = useState(false);
  const minZoom = 4;
  const maxZoom = 18;

  // Ref al token per usarlo dentro gli handler Leaflet senza ricreare l'effect
  const tokenRef = useRef(token);
  useEffect(() => { tokenRef.current = token; }, [token]);

  // Cache in-memory dei dettagli operatore (description, photo, address, phone, email, website)
  // Il search_all della mappa è volutamente leggero (no description/photo) per evitare
  // CPU timeout del Deno edge-runtime sul paging — i dettagli ricchi arrivano al click.
  // Cap a MAX entries per evitare crescita illimitata dopo sessioni lunghe (FIFO via ordine di inserzione della Map).
  const operatorDetailsCache = useRef(new Map<string, {
    description?: string | null;
    photo_url?: string | null;
    address?: string | null;
    phone?: string | null;
    email?: string | null;
    website_url?: string | null;
    source?: string | null;
  }>());
  const OPERATOR_DETAILS_CACHE_MAX = 500;

  // Sync local zoom with store zoom
  useEffect(() => {
    setLocalZoom(zoomLevel);
  }, [zoomLevel]);

  // Handle zoom change from slider
  const handleZoomChange = useCallback((newZoom: number) => {
    if (map.current) {
      map.current.setZoom(newZoom);
      setLocalZoom(newZoom);
    }
  }, []);

  // Zoom in/out handlers
  const handleZoomIn = useCallback(() => {
    if (map.current && localZoom < maxZoom) {
      map.current.zoomIn();
    }
  }, [localZoom]);

  const handleZoomOut = useCallback(() => {
    if (map.current && localZoom > minZoom) {
      map.current.zoomOut();
    }
  }, [localZoom]);

  // Reset to Italy view
  const handleResetToItaly = useCallback(() => {
    if (map.current) {
      map.current.flyTo([41.9028, 12.4964], 6, {
        duration: 0.8,
        easeLinearity: 0.5,
      });
    }
  }, []);

  // Track drawer instance (for polygon)
  const shapeDrawer = useRef<L.Draw.Polygon | null>(null);

  // Click-click mode state for circle and rectangle
  const drawState = useRef<{
    firstPoint: L.LatLng | null;
    previewLayer: L.Circle | L.Rectangle | null;
  }>({ firstPoint: null, previewLayer: null });

  // Common shape options
  const shapeOptions = {
    color: "#284899",
    fillColor: "#284899",
    fillOpacity: 0.15,
    weight: 2,
  };

  // Helper to convert circle to polygon (for server-side filtering)
  const circleToPolygon = useCallback((center: L.LatLng, radius: number, numPoints = 64): GeoJSON.Polygon => {
    const coordinates: [number, number][] = [];
    for (let i = 0; i < numPoints; i++) {
      const angle = (i / numPoints) * 2 * Math.PI;
      const lat = center.lat + (radius / 111320) * Math.cos(angle);
      const lng = center.lng + (radius / (111320 * Math.cos(center.lat * Math.PI / 180))) * Math.sin(angle);
      coordinates.push([lng, lat]);
    }
    coordinates.push(coordinates[0]); // Close the ring
    return { type: "Polygon", coordinates: [coordinates] };
  }, []);

  // Click handler - click-click mode (memoized)
  const handleShapeClick = useCallback((e: L.LeafletMouseEvent) => {
    if (!map.current || !drawnItems.current) return;
    if (drawingShapeType !== "circle" && drawingShapeType !== "rectangle") return;

    if (!drawState.current.firstPoint) {
      // First click - set starting point
      drawState.current.firstPoint = e.latlng;
    } else {
      // Second click - create the shape
      const firstPoint = drawState.current.firstPoint;

      // Remove preview layer
      if (drawState.current.previewLayer && map.current) {
        map.current.removeLayer(drawState.current.previewLayer);
        drawState.current.previewLayer = null;
      }

      // Clear existing drawn items
      drawnItems.current.clearLayers();

      if (drawingShapeType === "circle") {
        const radius = firstPoint.distanceTo(e.latlng);
        const layer = L.circle(firstPoint, { ...shapeOptions, radius });
        drawnItems.current.addLayer(layer);

        // Convert to polygon for filtering
        const polygon = circleToPolygon(firstPoint, radius);
        setDrawnPolygon(polygon);
      } else {
        // Rectangle
        const bounds = L.latLngBounds(firstPoint, e.latlng);
        const layer = L.rectangle(bounds, shapeOptions);
        drawnItems.current.addLayer(layer);

        // Convert to polygon for filtering
        const geojson = layer.toGeoJSON();
        if (geojson.geometry.type === "Polygon") {
          setDrawnPolygon(geojson.geometry as GeoJSON.Polygon);
        }
      }

      // Reset state and exit drawing mode
      drawState.current.firstPoint = null;
      setIsDrawingMode(false);
    }
  }, [drawingShapeType, setDrawnPolygon, setIsDrawingMode, circleToPolygon]);

  // Mouse move - show preview after first click (memoized)
  const handleShapeMouseMove = useCallback((e: L.LeafletMouseEvent) => {
    if (!map.current || !drawState.current.firstPoint) return;
    if (drawingShapeType !== "circle" && drawingShapeType !== "rectangle") return;

    const firstPoint = drawState.current.firstPoint;

    // Remove old preview
    if (drawState.current.previewLayer && map.current) {
      map.current.removeLayer(drawState.current.previewLayer);
      drawState.current.previewLayer = null;
    }

    // Create preview
    if (drawingShapeType === "circle") {
      const radius = firstPoint.distanceTo(e.latlng);
      drawState.current.previewLayer = L.circle(firstPoint, {
        ...shapeOptions,
        fillOpacity: 0.1,
        dashArray: "5, 5",
        radius,
      }).addTo(map.current);
    } else {
      // Rectangle
      const bounds = L.latLngBounds(firstPoint, e.latlng);
      drawState.current.previewLayer = L.rectangle(bounds, {
        ...shapeOptions,
        fillOpacity: 0.1,
        dashArray: "5, 5",
      }).addTo(map.current);
    }
  }, [drawingShapeType]);

  // Watch isDrawingMode from store and activate appropriate drawer
  useEffect(() => {
    if (!map.current || !isMapLoaded) return;

    const currentMap = map.current;

    // Clean up any existing drawer first
    if (shapeDrawer.current) {
      shapeDrawer.current.disable();
      shapeDrawer.current = null;
    }

    // Clean up draw state
    if (drawState.current.previewLayer) {
      currentMap.removeLayer(drawState.current.previewLayer);
      drawState.current.previewLayer = null;
    }
    drawState.current.firstPoint = null;

    if (isDrawingMode) {
      // For circle and rectangle, use click-click mode
      if (drawingShapeType === "circle" || drawingShapeType === "rectangle") {
        currentMap.on("click", handleShapeClick);
        currentMap.on("mousemove", handleShapeMouseMove);
      } else {
        // For polygon, use native drawer
        shapeDrawer.current = new L.Draw.Polygon(currentMap as L.DrawMap, {
          allowIntersection: true,
          showArea: true,
          shapeOptions,
          drawError: {
            color: '#e1e100',
            message: '<strong>Errore:</strong> non puoi disegnare qui!'
          },
        });
        shapeDrawer.current.enable();
      }
    }

    return () => {
      // Always clean up handlers on unmount/re-run
      currentMap.off("click", handleShapeClick);
      currentMap.off("mousemove", handleShapeMouseMove);
      if (drawState.current.previewLayer) {
        currentMap.removeLayer(drawState.current.previewLayer);
        drawState.current.previewLayer = null;
      }
      if (shapeDrawer.current) {
        shapeDrawer.current.disable();
        shapeDrawer.current = null;
      }
    };
  }, [isDrawingMode, isMapLoaded, drawingShapeType, handleShapeClick, handleShapeMouseMove]);

  // Fade markers during drawing mode (more reliable than CSS)
  useEffect(() => {
    if (!markersLayer.current || !isMapLoaded) return;

    // Apply opacity to all markers in the layer
    markersLayer.current.eachLayer((layer) => {
      if (layer instanceof L.CircleMarker) {
        layer.setStyle({
          fillOpacity: isDrawingMode ? 0.15 : 0.9,
          opacity: isDrawingMode ? 0.25 : 1,
        });
      } else if (layer instanceof L.Marker) {
        // For div icons (clusters), set opacity on the element
        const element = layer.getElement();
        if (element) {
          element.style.opacity = isDrawingMode ? "0.25" : "1";
          element.style.pointerEvents = isDrawingMode ? "none" : "auto";
        }
      }
    });
  }, [isDrawingMode, isMapLoaded, clusters]);

  // Handle editing mode - enable direct layer editing with live updates
  // Note: leaflet-draw adds .editing property to layers at runtime (not in types)
  type EditableLayer = L.Layer & { editing?: { enable: () => void; disable: () => void } };

  useEffect(() => {
    if (!map.current || !isMapLoaded || !drawnItems.current) return;

    const layers = drawnItems.current.getLayers();
    const mapInstance = map.current;

    if (isEditingMode && layers.length > 0) {
      // Enable editing on each layer directly
      layers.forEach((layer) => {
        const editableLayer = layer as EditableLayer;
        if (editableLayer.editing) {
          editableLayer.editing.enable();
        }
      });

      // Function to update polygon from current layer state
      const updatePolygonFromLayers = () => {
        // Don't update during active drag operations - this causes errors in leaflet-draw
        if (isDraggingRef.current) return;

        // Prevent concurrent updates that could interfere with leaflet-draw
        if (isUpdatingPolygonRef.current) return;

        try {
          isUpdatingPolygonRef.current = true;
          const currentLayers = drawnItems.current?.getLayers() || [];
          currentLayers.forEach((layer) => {
            if (layer instanceof L.Circle) {
              const center = layer.getLatLng();
              const radius = layer.getRadius();
              // Convert circle to polygon using the helper function
              const polygon = circleToPolygon(center, radius);
              setDrawnPolygon(polygon);
            } else if (layer instanceof L.Polygon || layer instanceof L.Rectangle) {
              const geojson = layer.toGeoJSON();
              if (geojson.geometry.type === "Polygon") {
                setDrawnPolygon(geojson.geometry as GeoJSON.Polygon);
              }
            }
          });
        } catch {
          // Silently ignore errors during drag operations (e.g., radius not defined)
          // This can happen when leaflet-draw is in the middle of a drag operation
        } finally {
          // Reset flag after a short delay to allow leaflet-draw to complete
          setTimeout(() => {
            isUpdatingPolygonRef.current = false;
          }, 50);
        }
      };

      // Listen for edit events on the FeatureGroup
      drawnItems.current.on("edit", updatePolygonFromLayers);

      // For circles, we rely on leaflet-draw events and polling
      // Don't add custom event listeners that don't exist - they cause errors

      // Listen to map-level draw events for vertex/resize changes
      // EDITRESIZE is fired when resize ends, so it's safe to update
      mapInstance.on(L.Draw.Event.EDITRESIZE, () => {
        // Small delay to ensure leaflet-draw has finished updating the circle
        setTimeout(() => {
          updatePolygonFromLayers();
        }, 100);
      });
      mapInstance.on(L.Draw.Event.EDITMOVE, () => {
        // Update when circle is moved
        updatePolygonFromLayers();
      });
      mapInstance.on(L.Draw.Event.EDITVERTEX, () => {
        // Update when vertices are edited
        updatePolygonFromLayers();
      });

      // For circles, use a polling approach during editing to catch all changes
      // This ensures we catch radius changes even if events aren't fired properly
      // Clear any existing interval first
      if (circleUpdateIntervalRef.current) {
        clearInterval(circleUpdateIntervalRef.current);
        circleUpdateIntervalRef.current = null;
      }

      // Don't use polling for circles - it interferes with leaflet-draw during drag
      // Instead, rely only on leaflet-draw events (EDITRESIZE, EDITMOVE, EDITVERTEX)
      // These events are fired when the user finishes dragging, so they're safe

      // Cleanup function
      return () => {
        layers.forEach((layer) => {
          const editableLayer = layer as EditableLayer;
          if (editableLayer.editing) {
            editableLayer.editing.disable();
          }
          // No need to remove non-existent event listeners

          // Update lastRenderedPolygonRef to match current layer state after editing ends
          // This prevents the sync effect from unnecessarily recreating the layer
          if (layer instanceof L.Circle) {
            const center = layer.getLatLng();
            const radius = layer.getRadius();
            const coordinates: [number, number][] = [];
            const numPoints = 64;
            for (let i = 0; i < numPoints; i++) {
              const angle = (i / numPoints) * 2 * Math.PI;
              const lat = center.lat + (radius / 111320) * Math.cos(angle);
              const lng = center.lng + (radius / (111320 * Math.cos(center.lat * Math.PI / 180))) * Math.sin(angle);
              coordinates.push([lng, lat]);
            }
            coordinates.push(coordinates[0]);
            lastRenderedPolygonRef.current = JSON.stringify({ type: "Polygon", coordinates: [coordinates] });
          } else if (layer instanceof L.Polygon || layer instanceof L.Rectangle) {
            const geojson = layer.toGeoJSON();
            if (geojson.geometry.type === "Polygon") {
              lastRenderedPolygonRef.current = JSON.stringify(geojson.geometry);
            }
          }
        });
        drawnItems.current?.off("edit", updatePolygonFromLayers);
        mapInstance.off(L.Draw.Event.EDITRESIZE);
        mapInstance.off(L.Draw.Event.EDITMOVE);
        mapInstance.off(L.Draw.Event.EDITVERTEX);

        // Clear polling interval
        if (circleUpdateIntervalRef.current) {
          clearInterval(circleUpdateIntervalRef.current);
          circleUpdateIntervalRef.current = null;
        }
      };
    } else {
      // Disable editing on all layers
      layers.forEach((layer) => {
        const editableLayer = layer as EditableLayer;
        if (editableLayer.editing) {
          editableLayer.editing.disable();
        }
      });

      // Clear polling interval when exiting editing mode
      if (circleUpdateIntervalRef.current) {
        clearInterval(circleUpdateIntervalRef.current);
        circleUpdateIntervalRef.current = null;
      }
    }
  }, [isEditingMode, isMapLoaded, setDrawnPolygon, circleToPolygon]);

  // Stop polling when polygon is cleared (e.g., when clearing filters)
  useEffect(() => {
    if (!drawnPolygon && circleUpdateIntervalRef.current) {
      clearInterval(circleUpdateIntervalRef.current);
      circleUpdateIntervalRef.current = null;
    }
  }, [drawnPolygon]);

  // Track the last polygon we rendered to detect changes from store
  const lastRenderedPolygonRef = useRef<string | null>(null);

  // Sync drawn layers with polygon from store (e.g., when loading saved search)
  useEffect(() => {
    if (!drawnItems.current || !map.current || !isMapLoaded) return;

    // Skip syncing when in editing mode - live edits flow to the store
    // but we don't want to recreate layers and lose the editing state
    if (isEditingMode) {
      return;
    }

    // Serialize current polygon to compare
    const currentPolygonStr = drawnPolygon ? JSON.stringify(drawnPolygon) : null;

    // If polygon was cleared from store, clear the visual layer too
    if (!drawnPolygon) {
      if (drawnItems.current.getLayers().length > 0) {
        drawnItems.current.clearLayers();
      }
      lastRenderedPolygonRef.current = null;
      return;
    }

    // Skip if the polygon hasn't changed (to avoid re-rendering on every update)
    if (currentPolygonStr === lastRenderedPolygonRef.current) {
      return;
    }

    // Clear existing layers before creating new ones
    // This handles the case when loading a different saved search
    drawnItems.current.clearLayers();

    const coords = drawnPolygon.coordinates[0];

    // Check if it's likely a circle (many points, roughly circular)
    // Circles are saved as 64-point polygons
    if (coords.length === 65) { // 64 points + closing point
      // Try to reconstruct circle from polygon
      // Find center (average of all points) and radius
      let sumLat = 0, sumLng = 0;
      for (let i = 0; i < coords.length - 1; i++) {
        sumLng += coords[i][0];
        sumLat += coords[i][1];
      }
      const centerLat = sumLat / (coords.length - 1);
      const centerLng = sumLng / (coords.length - 1);
      const center = L.latLng(centerLat, centerLng);

      // Calculate radius from first point
      const firstPoint = L.latLng(coords[0][1], coords[0][0]);
      const radius = center.distanceTo(firstPoint);

      const circle = L.circle(center, {
        ...shapeOptions,
        radius,
      });
      drawnItems.current.addLayer(circle);
    } else if (coords.length === 5) {
      // Check if it's a rectangle (4 points + closing point)
      // Rectangles have 4 corners with only 2 unique lat and 2 unique lng values
      const lats = [...new Set(coords.slice(0, 4).map(c => c[1]))];
      const lngs = [...new Set(coords.slice(0, 4).map(c => c[0]))];

      if (lats.length === 2 && lngs.length === 2) {
        const bounds = L.latLngBounds(
          [Math.min(...lats), Math.min(...lngs)],
          [Math.max(...lats), Math.max(...lngs)]
        );
        const rect = L.rectangle(bounds, shapeOptions);
        drawnItems.current.addLayer(rect);
      } else {
        // It's a polygon with 4 points
        const latLngs = coords.slice(0, -1).map(c => L.latLng(c[1], c[0]));
        const polygon = L.polygon(latLngs, shapeOptions);
        drawnItems.current.addLayer(polygon);
      }
    } else {
      // Regular polygon
      const latLngs = coords.slice(0, -1).map(c => L.latLng(c[1], c[0]));
      const polygon = L.polygon(latLngs, shapeOptions);
      drawnItems.current.addLayer(polygon);
    }

    // Update the ref to track what we've rendered
    lastRenderedPolygonRef.current = currentPolygonStr;

    // Fit the map to the drawn area
    const bounds = drawnItems.current.getBounds();
    if (bounds.isValid()) {
      map.current.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
    }
  }, [drawnPolygon, isMapLoaded, isEditingMode]);

  // Debounced function to update bounds and zoom
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateMapState = useCallback(
    debounce((mapInstance: L.Map) => {
      const bounds = mapInstance.getBounds();
      const zoom = mapInstance.getZoom();

      setMapBounds({
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest(),
      });
      setZoomLevel(zoom);
    }, 200),
    [setMapBounds, setZoomLevel]
  );

  // Get current tile URL based on theme
  const getTileUrl = () => {
    const currentTheme = theme === "system" ? resolvedTheme : theme;
    return currentTheme === "light" ? TILE_LAYERS.light : TILE_LAYERS.dark;
  };

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    // Create map centered on Italy
    map.current = L.map(mapContainer.current, {
      center: [41.9028, 12.4964], // Rome
      zoom: 6,
      minZoom: 4,
      maxZoom: 18,
      zoomControl: false,
      attributionControl: false,
    });

    // Add tiles - OpenStreetMap standard per light, CartoDB Dark Matter per dark
    const tileUrl = getTileUrl();
    const isOSM = tileUrl.includes('openstreetmap.org');
    const subdomains = isOSM ? 'abc' : 'abcd'; // OSM usa 'abc', CartoDB usa 'abcd'

    tileLayer.current = L.tileLayer(tileUrl, {
      maxZoom: 20,
      subdomains: subdomains,
      attribution: isOSM
        ? '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        : undefined,
    }).addTo(map.current);
    currentTileUrl.current = tileUrl;

    // Create markers layer
    markersLayer.current = L.layerGroup().addTo(map.current);

    // Create a custom pane for hover ghost markers with high z-index (above clusters)
    map.current.createPane("ghostMarkerPane");
    const ghostPane = map.current.getPane("ghostMarkerPane");
    if (ghostPane) {
      ghostPane.style.zIndex = "700"; // Above markerPane (600) and shadowPane (500)
    }

    // Create a custom pane for zone boundary (below overlayPane 400, below drawn shapes)
    map.current.createPane("zoneBoundaryPane");
    const zonePane = map.current.getPane("zoneBoundaryPane");
    if (zonePane) {
      zonePane.style.zIndex = "350";
    }

    drawnItems.current = new L.FeatureGroup().addTo(map.current);

    // Handle draw events (for polygon drawer)
    map.current.on(L.Draw.Event.CREATED, (e: L.LeafletEvent) => {
      const event = e as L.DrawEvents.Created;
      const layer = event.layer;

      drawnItems.current?.clearLayers();
      drawnItems.current?.addLayer(layer);

      // Handle polygon (circle/rectangle now use click-click mode)
      const geojson = (layer as L.Polygon).toGeoJSON();
      if (geojson.geometry.type === "Polygon") {
        setDrawnPolygon(geojson.geometry as GeoJSON.Polygon);
      }
      setIsDrawingMode(false);
    });

    // Handle edit events
    map.current.on(L.Draw.Event.EDITED, (e: L.LeafletEvent) => {
      const event = e as L.DrawEvents.Edited;
      event.layers.eachLayer((layer) => {
        if (layer instanceof L.Circle) {
          const center = layer.getLatLng();
          const radius = layer.getRadius();
          const polygon = circleToPolygon(center, radius);
          setDrawnPolygon(polygon);
        } else if (layer instanceof L.Polygon || layer instanceof L.Rectangle) {
          const geojson = layer.toGeoJSON();
          if (geojson.geometry.type === "Polygon") {
            setDrawnPolygon(geojson.geometry as GeoJSON.Polygon);
          }
        }
      });
      setIsEditingMode(false);
    });

    map.current.on(L.Draw.Event.DELETED, () => {
      setDrawnPolygon(null);
    });

    // Handle map move/zoom
    map.current.on("moveend", () => {
      if (map.current) {
        updateMapState(map.current);
      }
    });

    map.current.on("zoomend", () => {
      if (map.current) {
        updateMapState(map.current);
        setLocalZoom(map.current.getZoom());
      }
    });

    // Set initial bounds and zoom immediately (not debounced)
    const bounds = map.current.getBounds();
    const zoom = map.current.getZoom();
    setMapBounds({
      north: bounds.getNorth(),
      south: bounds.getSouth(),
      east: bounds.getEast(),
      west: bounds.getWest(),
    });
    setZoomLevel(zoom);

    setIsMapLoaded(true);

    return () => {
      map.current?.remove();
      map.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setDrawnPolygon, setIsDrawingMode, updateMapState]);

  // Update tile layer when theme changes
  useEffect(() => {
    if (!map.current || !tileLayer.current || !currentTileUrl.current) return;

    const newUrl = getTileUrl();
    // Se l'URL non è cambiato, non fare nulla
    if (newUrl === currentTileUrl.current) return;

    const isOSM = newUrl.includes('openstreetmap.org');
    const currentIsOSM = currentTileUrl.current.includes('openstreetmap.org');

    // Se cambia il tipo di provider (OSM <-> CartoDB), ricrea il layer
    // perché hanno subdomains diversi
    if (isOSM !== currentIsOSM) {
      map.current.removeLayer(tileLayer.current);
      const subdomains = isOSM ? 'abc' : 'abcd';
      tileLayer.current = L.tileLayer(newUrl, {
        maxZoom: 20,
        subdomains: subdomains,
        attribution: isOSM
          ? '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          : undefined,
      }).addTo(map.current);
      currentTileUrl.current = newUrl;
    } else {
      // Stesso tipo di provider, basta cambiare l'URL
      tileLayer.current.setUrl(newUrl);
      currentTileUrl.current = newUrl;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme, resolvedTheme]);

  // Render clusters and markers with diffing
  // Note: renderizziamo anche durante isLoading per aggiornare in modo incrementale
  // i marker man mano che arrivano i batch progressivi (PAGE_SIZE=20000). Bloccare
  // qui lasciava la mappa con i pallini vecchi finché il paging non finiva,
  // producendo l'effetto "pallini fantasma" dopo cambi filtri ripetuti.
  useEffect(() => {
    if (!map.current || !markersLayer.current || !isMapLoaded) return;
    if (!clusters) return;

    const updateMarkers = () => {
      const shouldAnimate = isFirstRender.current;

      // Build map of new features by key
      const newFeatures = new Map<string, MapFeature>();
      clusters.forEach((feature) => {
        const key = getFeatureKey(feature);
        newFeatures.set(key, feature);
      });

      // Find markers to remove (exist in current but not in new)
      const keysToRemove: string[] = [];
      markersMap.current.forEach((marker, key) => {
        if (!newFeatures.has(key)) {
          markersLayer.current?.removeLayer(marker);
          keysToRemove.push(key);
        }
      });
      keysToRemove.forEach(key => markersMap.current.delete(key));

      // Add or update markers in batches to prevent blocking
      const BATCH_SIZE = 100; // Process markers in batches
      const featuresToAdd: MapFeature[] = [];

      clusters.forEach((feature) => {
        const key = getFeatureKey(feature);
        // Skip if marker already exists with same key
        if (!markersMap.current.has(key)) {
          featuresToAdd.push(feature);
        }
      });

      // Process in batches using requestAnimationFrame for smooth rendering
      let batchIndex = 0;
      const processBatch = () => {
        const batch = featuresToAdd.slice(batchIndex, batchIndex + BATCH_SIZE);

        batch.forEach((feature) => {
          const key = getFeatureKey(feature);
          const [lng, lat] = feature.geometry.coordinates;

          if (isCluster(feature)) {
            // Create new cluster marker
            const { point_count, uniformTypology, uniformMacroCategory, cluster_id } = feature.properties;
            const { size, sizeClass } = getClusterSize(point_count);
            const displayCount = formatCount(point_count);
            const animateClass = shouldAnimate ? "animate-in" : "";

            // Calculate pie chart when there are mixed typologies or categories
            // Show pie chart if: no uniform typology (even if uniform macro category exists)
            let pieChartCSS: string | undefined;
            if (!uniformTypology && getClusterLeaves && point_count > 1) {
              try {
                // Sample up to 60 operators to calculate percentages (for performance)
                const sampleSize = Math.min(point_count, 60);
                const operators = getClusterLeaves(cluster_id, sampleSize);
                if (operators.length > 0) {
                  const percentages = calculateCategoryPercentages(operators);
                  // Always show pie chart if we have percentages (even if single category, shows distribution)
                  if (percentages.length > 0) {
                    pieChartCSS = generatePieChartCSS(percentages);
                  }
                }
              } catch (error) {
                console.warn(`Failed to calculate pie chart for cluster ${cluster_id}:`, error);
              }
            }

            // Get color based on uniform typology (if all points have same type), macro category, pie chart, or size
            const colorStyle = getClusterColor(uniformTypology, uniformMacroCategory, sizeClass, pieChartCSS);

            const clusterIcon = L.divIcon({
              html: `<div class="supercluster-marker ${sizeClass} ${animateClass}" style="${colorStyle}"><span>${displayCount}</span></div>`,
              className: "supercluster-icon",
              iconSize: L.point(size, size),
              iconAnchor: L.point(size / 2, size / 2),
            });

            const marker = L.marker([lat, lng], { icon: clusterIcon });

            // Apply drawing mode opacity if active
            if (isDrawingMode) {
              const element = marker.getElement?.();
              if (element) {
                element.style.opacity = "0.25";
                element.style.pointerEvents = "none";
              }
            }

            marker.on("click", () => {
              const expansionZoom = getClusterExpansionZoom(feature.properties.cluster_id);
              if (map.current) {
                map.current.flyTo([lat, lng], expansionZoom, { duration: 0.5 });
              }
              onClusterClick?.(feature, expansionZoom);
            });

            markersLayer.current?.addLayer(marker);
            markersMap.current.set(key, marker);

            // Apply opacity after adding to map (element available now)
            if (isDrawingMode) {
              const element = marker.getElement();
              if (element) {
                element.style.opacity = "0.25";
                element.style.pointerEvents = "none";
              }
            }
          } else {
            // Create new operator marker
            const operator = feature.properties as OperatorWithCoords;
            const color = getMarkerColor(operator.typology, operator.macro_category);
            const isSelected = operator.id === selectedOperator?.id;

            const marker = L.circleMarker([lat, lng], {
              radius: 8,
              fillColor: color,
              color: "#ffffff",
              weight: isSelected ? 3 : 2,
              opacity: isDrawingMode ? 0.25 : 1,
              fillOpacity: isDrawingMode ? 0.15 : 0.9,
            });

            const typologyIcon = getTypologyIconSvg(operator.typology);

            // Costruisce l'HTML del popup. Accetta dettagli opzionali (description,
            // photo_url, address, phone, email, website_url) che arrivano on-demand
            // via get_operator al primo click sul marker.
            const buildPopupContent = (extra?: {
              description?: string | null;
              photo_url?: string | null;
              address?: string | null;
              phone?: string | null;
              email?: string | null;
              website_url?: string | null;
              source?: string | null;
            }) => {
              const photoUrl = extra?.photo_url;
              const description = extra?.description;
              const address = extra?.address;
              const phone = extra?.phone;
              const website = extra?.website_url;
              const source = extra?.source ?? operator.source;
              const sourceAttribution = (source === "FederTerme" || source === "Ticketone")
                ? `<p class="source-attribution">Dato fornito da: <strong>${source}</strong></p>`
                : "";
              const descTruncated = description && description.length > 220
                ? description.slice(0, 220).trim() + "…"
                : description;
              return `
          <div class="marker-popup">
            ${photoUrl ? `<div class="popup-photo"><img src="${photoUrl}" alt="" onerror="this.parentElement.style.display='none'"/></div>` : ""}
            <h3><span class="type-icon" style="color: ${color}">${typologyIcon}</span>${operator.name}</h3>
            ${operator.city ? `<p class="location"><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>${operator.city}${operator.region ? `, ${operator.region}` : ""}</p>` : ""}
            ${address ? `<p class="address"><svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>${address}</p>` : ""}
            ${phone ? `<p class="phone"><svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg><a href="tel:${phone}">${phone}</a></p>` : ""}
            ${website ? `<p class="website"><svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15 15 0 0 1 4 10 15 15 0 0 1-4 10 15 15 0 0 1-4-10 15 15 0 0 1 4-10z"/></svg><a href="${website}" target="_blank" rel="noopener">${website.replace(/^https?:\/\//, "")}</a></p>` : ""}
            ${descTruncated ? `<p class="description">${descTruncated}</p>` : ""}
            ${sourceAttribution}
          </div>
        `;
            };

            // Costruzione iniziale (minimale) + riempimento on-demand al primo click
            const initialCached = operatorDetailsCache.current.get(operator.id);
            marker.bindPopup(buildPopupContent(initialCached), {
              className: "custom-popup",
              closeButton: true,
              maxWidth: 280,
            });

            marker.on("popupopen", async () => {
              const cached = operatorDetailsCache.current.get(operator.id);
              if (cached) return; // già arricchito
              const currentToken = tokenRef.current;
              if (!currentToken) return;
              try {
                const details = await queryOperatorsProtected<{
                  description?: string | null;
                  photo_url?: string | null;
                  address?: string | null;
                  phone?: string | null;
                  email?: string | null;
                  website_url?: string | null;
                  source?: string | null;
                } | null>(currentToken, "get_operator", { id: operator.id });
                if (!details) return;
                const cache = operatorDetailsCache.current;
                if (cache.size >= OPERATOR_DETAILS_CACHE_MAX) {
                  const oldestKey = cache.keys().next().value;
                  if (oldestKey !== undefined) cache.delete(oldestKey);
                }
                cache.set(operator.id, details);
                marker.setPopupContent(buildPopupContent(details));
              } catch {
                // se fallisce, resta il popup minimale
              }
            });

            marker.on("mouseover", function (this: L.CircleMarker) {
              this.setRadius(10);
              this.setStyle({ weight: 3 });
              // Set hovered operator ID for list highlighting
              setHoveredOperatorId(operator.id);
            });

            marker.on("mouseout", function (this: L.CircleMarker) {
              const isCurrentlySelected = operator.id === previousSelectedId.current;
              const isCurrentlyHovered = operator.id === previousHoveredId.current;
              // Only reset if not being hovered from list
              if (!isCurrentlyHovered) {
                this.setRadius(8);
                this.setStyle({ weight: isCurrentlySelected ? 3 : 2, color: "#ffffff" });
              }
              // Clear hovered operator ID
              setHoveredOperatorId(null);
            });

            marker.on("click", () => {
              // For single markers, just open popup without zooming
              marker.openPopup();
              onOperatorClick?.(operator);
            });

            markersLayer.current?.addLayer(marker);
            markersMap.current.set(key, marker);
          }
        });

        batchIndex += BATCH_SIZE;

        // Continue processing next batch if there are more features
        if (batchIndex < featuresToAdd.length) {
          requestAnimationFrame(processBatch);
        } else {
          // After first render, mark as complete
          if (isFirstRender.current && clusters.length > 0) {
            isFirstRender.current = false;
          }
        }
      };

      // Start processing batches
      if (featuresToAdd.length > 0) {
        requestAnimationFrame(processBatch);
      } else {
        // No new markers to add
        if (isFirstRender.current && clusters.length > 0) {
          isFirstRender.current = false;
        }
      }
    };

    // Use requestAnimationFrame to batch the entire update
    requestAnimationFrame(updateMarkers);
  }, [clusters, isMapLoaded, isDrawingMode, onOperatorClick, onClusterClick, getClusterExpansionZoom, selectedOperator, setHoveredOperatorId]);

  // Handle selection from list - fly to marker and open popup
  useEffect(() => {
    if (!map.current || !isMapLoaded) return;

    // Clean up any existing ghost markers
    if (flyingGhostMarker.current) {
      map.current.removeLayer(flyingGhostMarker.current);
      flyingGhostMarker.current = null;
    }

    // Reset previous marker style
    if (previousSelectedId.current && previousSelectedId.current !== selectedOperator?.id) {
      // Find by operator ID pattern
      markersMap.current.forEach((marker, key) => {
        if (key === `o_${previousSelectedId.current}` && marker instanceof L.CircleMarker) {
          marker.setStyle({ weight: 2, color: "#ffffff" });
          marker.closePopup();
        }
      });
    }

    if (!selectedOperator) {
      previousSelectedId.current = null;
      return;
    }

    const selectedId = selectedOperator.id;
    previousSelectedId.current = selectedId;

    if (selectedOperator.lat && selectedOperator.lng) {
      const currentZoom = map.current.getZoom();
      const targetZoom = Math.max(currentZoom, 14);
      const mapInstance = map.current;

      // Use setView for instant positioning when marker exists, flyTo for smooth navigation
      const markerKey = `o_${selectedId}`;
      const existingMarker = markersMap.current.get(markerKey);

      if (existingMarker && existingMarker instanceof L.CircleMarker) {
        // Marker already visible - don't zoom, just open popup and highlight
        existingMarker.openPopup();
        existingMarker.setStyle({ weight: 3, color: "#ffffff" });
      } else {
        // Marker is clustered - zoom first, then find the marker
        mapInstance.flyTo([selectedOperator.lat, selectedOperator.lng], targetZoom, {
          duration: 0.5,
          easeLinearity: 0.5,
        });

        // Wait for zoom to complete and clusters to recalculate
        const onMoveEnd = () => {
          mapInstance.off('moveend', onMoveEnd);
          // Give time for markers to render after cluster expansion
          setTimeout(() => {
            const marker = markersMap.current.get(markerKey);
            if (marker && marker instanceof L.CircleMarker) {
              marker.openPopup();
              marker.setStyle({ weight: 3, color: "#ffffff" });
            }
          }, 150);
        };
        mapInstance.on('moveend', onMoveEnd);
      }
    }
  }, [selectedOperator, isMapLoaded]);

  // ============================================
  // DMO area overlay
  // Quando l'operatore selezionato e' una DMO/Organizzazione Turistica/
  // Destinazione, fetcha il poligono unione (via RPC get_dmo_area) e lo
  // disegna come layer GeoJSON. Cambia selezione -> rimuove vecchio,
  // disegna nuovo. Nessuna selezione -> rimuove layer.
  // ============================================
  const isAreaOperator = isDmoMacroCategory(selectedOperator?.macro_category);
  const { data: dmoAreaGeometry } = useDmoArea(
    isAreaOperator ? selectedOperator?.name : null,
    isAreaOperator,
  );

  useEffect(() => {
    if (!map.current || !isMapLoaded) return;
    if (!isAreaOperator || !dmoAreaGeometry) return;

    const macroColor = getCategoryConfig(selectedOperator?.macro_category).color;
    const layer = L.geoJSON(dmoAreaGeometry as GeoJSON.GeoJsonObject, {
      style: {
        color: macroColor,
        weight: 1.5,
        opacity: 0.7,
        fillColor: macroColor,
        fillOpacity: 0.2,
        interactive: false,
      },
      pane: "overlayPane",
    });
    layer.addTo(map.current);
    dmoAreaLayerRef.current = layer;

    // Fit the area: some DMOs cover entire provinces/regions far from the
    // marker location. maxZoom avoids zooming too close on small areas
    // (e.g. a destination covering 4 comuni).
    const bounds = layer.getBounds();
    if (bounds.isValid()) {
      map.current.fitBounds(bounds, { padding: [60, 60], maxZoom: 11 });
    }

    return () => {
      if (dmoAreaLayerRef.current && map.current) {
        map.current.removeLayer(dmoAreaLayerRef.current);
        dmoAreaLayerRef.current = null;
      }
    };
  }, [dmoAreaGeometry, isAreaOperator, selectedOperator?.macro_category, isMapLoaded]);

  // Auto-zoom on geographic search (city/region)
  // Skip when zoneBoundary is present — the boundary effect handles zoom
  useEffect(() => {
    if (!map.current || !isMapLoaded || !filters.searchText) return;
    if (zoneBoundary) return; // Zone boundary fitBounds takes priority

    if (filteredOperators.length === 0) return;

    // Calculate bounding box from filtered operators
    const lats = filteredOperators.map(op => op.lat).filter((lat): lat is number => lat != null && !isNaN(lat));
    const lngs = filteredOperators.map(op => op.lng).filter((lng): lng is number => lng != null && !isNaN(lng));

    if (lats.length > 0 && lngs.length > 0) {
      const bounds = L.latLngBounds(
        [Math.min(...lats), Math.min(...lngs)],
        [Math.max(...lats), Math.max(...lngs)]
      );
      map.current.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
    }
  }, [filters.searchText, filteredOperators, isMapLoaded, zoneBoundary]);

  // Render zone boundary polygon (administrative border from Nominatim)
  useEffect(() => {
    if (!map.current || !isMapLoaded) return;

    // Remove previous boundary layer
    if (zoneBoundaryLayerRef.current) {
      map.current.removeLayer(zoneBoundaryLayerRef.current);
      zoneBoundaryLayerRef.current = null;
    }

    if (!zoneBoundary) return;

    // Create GeoJSON layer with dashed style to distinguish from user-drawn shapes
    const layer = L.geoJSON(zoneBoundary as GeoJSON.GeoJsonObject, {
      style: {
        color: "#284899",
        fillColor: "#284899",
        fillOpacity: 0.08,
        weight: 2,
        dashArray: "6, 4",
        pane: "zoneBoundaryPane",
      },
    });

    layer.addTo(map.current);
    zoneBoundaryLayerRef.current = layer;

    // Zoom to boundary bounds
    const bounds = layer.getBounds();
    if (bounds.isValid()) {
      map.current.fitBounds(bounds, { padding: [50, 50], maxZoom: 13 });
    }

    return () => {
      if (map.current && zoneBoundaryLayerRef.current) {
        map.current.removeLayer(zoneBoundaryLayerRef.current);
        zoneBoundaryLayerRef.current = null;
      }
    };
  }, [zoneBoundary, isMapLoaded]);

  // Track currently hovered marker for cleanup
  const previousHoveredId = useRef<string | null>(null);
  // Temporary hover marker for clustered operators
  const hoverGhostMarker = useRef<L.CircleMarker | null>(null);

  // Handle ESC key to close all open popups and deselect operator
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && map.current && markersLayer.current) {
        // Close all open popups
        markersLayer.current.eachLayer((layer) => {
          if (layer instanceof L.CircleMarker || layer instanceof L.Marker) {
            if (layer.isPopupOpen()) {
              layer.closePopup();
            }
          }
        });
        // Deselect operator
        if (selectedOperator) {
          setSelectedOperator(null);
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isMapLoaded, selectedOperator, setSelectedOperator]);

  // Handle hover from list - highlight marker on map (or show ghost marker if clustered)
  useEffect(() => {
    if (!map.current || !isMapLoaded) return;

    // Clean up previous ghost marker
    if (hoverGhostMarker.current) {
      map.current.removeLayer(hoverGhostMarker.current);
      hoverGhostMarker.current = null;
    }

    // Reset previous hovered marker style
    if (previousHoveredId.current && previousHoveredId.current !== hoveredOperatorId) {
      const markerKey = `o_${previousHoveredId.current}`;
      const prevMarker = markersMap.current.get(markerKey);
      if (prevMarker && prevMarker instanceof L.CircleMarker) {
        // Don't reset if it's the selected marker
        const isSelected = previousHoveredId.current === selectedOperator?.id;
        prevMarker.setRadius(8);
        prevMarker.setStyle({ weight: isSelected ? 3 : 2, color: "#ffffff" });
      }
    }

    previousHoveredId.current = hoveredOperatorId;

    if (!hoveredOperatorId) return;

    const markerKey = `o_${hoveredOperatorId}`;
    const marker = markersMap.current.get(markerKey);

    if (marker && marker instanceof L.CircleMarker) {
      // Marker exists on map - highlight it
      marker.setRadius(12);
      marker.setStyle({
        weight: 4,
        color: "#284899", // Amber highlight color
      });
      marker.bringToFront();
    } else {
      // Marker is clustered - show a ghost marker at the operator's position
      const operator = results.find(op => op.id === hoveredOperatorId);
      if (operator && operator.lat && operator.lng) {
        const color = getMarkerColor(operator.typology, operator.macro_category);

        // Create a pulsing ghost marker in the high z-index pane
        hoverGhostMarker.current = L.circleMarker([operator.lat, operator.lng], {
          radius: 14,
          fillColor: color,
          color: "#284899",
          weight: 3,
          opacity: 1,
          fillOpacity: 0.9,
          className: "hover-ghost-marker",
          pane: "ghostMarkerPane", // Use the custom high z-index pane
        });

        hoverGhostMarker.current.addTo(map.current);
      }
    }
  }, [hoveredOperatorId, isMapLoaded, selectedOperator, results]);

  return (
    <div className="relative w-full h-full z-0">
      <style>{`
        /* Supercluster marker styles */
        .supercluster-icon {
          background: transparent;
        }
        
        .supercluster-marker {
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          font-weight: 700;
          color: #fff;
          text-shadow: 0 1px 2px rgba(0,0,0,0.3);
          box-shadow: 
            0 4px 14px rgba(0,0,0,0.25),
            0 0 0 3px rgba(255,255,255,0.2),
            inset 0 1px 0 rgba(255,255,255,0.2);
          transition: transform 0.15s ease, box-shadow 0.15s ease;
          cursor: pointer;
        }
        
        /* Animation only for new data loading */
        .supercluster-marker.animate-in {
          animation: marker-pop-in 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
        
        @keyframes marker-pop-in {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
        
        /* Loading Spinner */
        .loading-spinner {
          width: 48px;
          height: 48px;
          border: 4px solid hsl(var(--muted));
          border-top-color: hsl(var(--primary));
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        .supercluster-marker:hover {
          transform: scale(1.15);
          box-shadow: 
            0 8px 24px rgba(0,0,0,0.35),
            0 0 0 4px rgba(255,255,255,0.3),
            inset 0 1px 0 rgba(255,255,255,0.2);
        }
        
        .supercluster-marker:active {
          transform: scale(1.05);
        }
        
        .cluster-small {
          width: 32px;
          height: 32px;
          font-size: 11px;
        }
        
        .cluster-medium {
          width: 38px;
          height: 38px;
          font-size: 12px;
        }
        
        .cluster-large {
          width: 46px;
          height: 46px;
          font-size: 13px;
        }
        
        .cluster-xlarge {
          width: 54px;
          height: 54px;
          font-size: 14px;
        }
        
        /* Popup styles */
        .custom-popup .leaflet-popup-content-wrapper {
          background-color: hsl(var(--card));
          border-radius: 12px;
          border: 1px solid hsl(var(--border));
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.4);
          color: hsl(var(--foreground));
        }
        
        .custom-popup .leaflet-popup-tip {
          background-color: hsl(var(--card));
          border: 1px solid hsl(var(--border));
        }
        
        .custom-popup .leaflet-popup-close-button {
          color: hsl(var(--muted-foreground)) !important;
        }
        
        .custom-popup .leaflet-popup-close-button:hover {
          color: hsl(var(--foreground)) !important;
        }
        
        .marker-popup {
          padding: 12px;
          min-width: 120px;
        }
        
        .marker-popup h3 {
          font-weight: 600;
          margin: 0 0 6px 0;
          font-size: 14px;
          color: hsl(var(--foreground));
          display: flex;
          align-items: center;
          gap: 6px;
        }
        
        .marker-popup h3 .type-icon {
          display: inline-flex;
          align-items: center;
          flex-shrink: 0;
        }
        
        .marker-popup h3 .type-icon svg {
          flex-shrink: 0;
        }
        
        .marker-popup .location {
          margin: 6px 0 0 0;
          font-size: 12px;
          color: hsl(var(--muted-foreground));
          display: flex;
          align-items: center;
          gap: 6px;
        }
        
        .marker-popup .location svg {
          flex-shrink: 0;
        }
        
        .marker-popup .address {
          margin: 4px 0 0 0;
          font-size: 11px;
          color: hsl(var(--muted-foreground) / 0.8);
          display: flex;
          align-items: flex-start;
          gap: 6px;
        }
        
        .marker-popup .address svg {
          flex-shrink: 0;
          margin-top: 1px;
        }

        .marker-popup .popup-photo {
          margin: -14px -14px 8px -14px;
          width: calc(100% + 28px);
          height: 120px;
          overflow: hidden;
          background: hsl(var(--muted) / 0.3);
        }
        .marker-popup .popup-photo img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        .marker-popup .phone,
        .marker-popup .website {
          margin: 4px 0 0 0;
          font-size: 11px;
          color: hsl(var(--muted-foreground) / 0.9);
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .marker-popup .phone a,
        .marker-popup .website a {
          color: inherit;
          text-decoration: none;
          word-break: break-all;
        }
        .marker-popup .phone a:hover,
        .marker-popup .website a:hover {
          text-decoration: underline;
        }

        .marker-popup .description {
          margin: 8px 0 0 0;
          padding-top: 8px;
          border-top: 1px solid hsl(var(--border) / 0.5);
          font-size: 11px;
          line-height: 1.4;
          color: hsl(var(--foreground) / 0.85);
        }

        .marker-popup .source-attribution {
          margin: 8px 0 0 0;
          padding-top: 6px;
          border-top: 1px solid hsl(var(--border) / 0.3);
          font-size: 10px;
          color: hsl(var(--muted-foreground));
        }

        /* Custom Zoom Control Styles */
        .zoom-control-container {
          position: absolute;
          left: 12px;
          bottom: 12px;
          z-index: 500;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          background: hsl(var(--card));
          border: 1px solid hsl(var(--border));
          border-radius: 12px;
          padding: 8px 6px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          backdrop-filter: blur(8px);
        }
        
        .zoom-level-display {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 28px;
          background: linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.8));
          border-radius: 6px;
          margin-bottom: 4px;
        }
        
        .zoom-level-value {
          font-family: 'JetBrains Mono', 'SF Mono', monospace;
          font-size: 12px;
          font-weight: 700;
          color: hsl(var(--primary-foreground));
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
        }
        
        .zoom-slider-track {
          position: relative;
          height: 120px;
          width: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 4px 0;
        }
        
        .zoom-slider {
          -webkit-appearance: none;
          appearance: none;
          width: 120px;
          height: 6px;
          background: hsl(var(--secondary));
          border-radius: 3px;
          transform: rotate(-90deg);
          transform-origin: center center;
          cursor: pointer;
          outline: none;
        }
        
        .zoom-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 16px;
          height: 16px;
          background: linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.85));
          border: 2px solid hsl(var(--background));
          border-radius: 50%;
          cursor: grab;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        
        .zoom-slider::-webkit-slider-thumb:hover {
          transform: scale(1.15);
          box-shadow: 0 3px 10px rgba(0, 0, 0, 0.35);
        }
        
        .zoom-slider::-webkit-slider-thumb:active {
          cursor: grabbing;
          transform: scale(1.1);
        }
        
        .zoom-slider::-moz-range-thumb {
          width: 16px;
          height: 16px;
          background: linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.85));
          border: 2px solid hsl(var(--background));
          border-radius: 50%;
          cursor: grab;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        
        .zoom-slider::-moz-range-thumb:hover {
          transform: scale(1.15);
        }
        
        .zoom-slider::-moz-range-track {
          background: hsl(var(--secondary));
          height: 6px;
          border-radius: 3px;
        }
        
        .zoom-ticks {
          position: absolute;
          left: 6px;
          top: 4px;
          bottom: 4px;
          width: 4px;
          display: flex;
          flex-direction: column-reverse;
          justify-content: space-between;
          pointer-events: none;
        }
        
        .zoom-tick {
          width: 4px;
          height: 2px;
          background: hsl(var(--muted-foreground) / 0.3);
          border-radius: 1px;
        }
        
        .zoom-tick-active {
          background: hsl(var(--primary));
          width: 6px;
        }
        
        .zoom-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          background: hsl(var(--secondary));
          border: none;
          border-radius: 8px;
          color: hsl(var(--foreground));
          cursor: pointer;
          transition: all 0.15s ease;
        }
        
        .zoom-btn:hover:not(:disabled) {
          background: hsl(var(--secondary) / 0.8);
          transform: scale(1.05);
        }
        
        .zoom-btn:active:not(:disabled) {
          transform: scale(0.95);
        }
        
        .zoom-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        
        /* Hide default Leaflet draw toolbar */
        .leaflet-draw {
          display: none !important;
        }
        
        /* Pulsing ghost marker for clustered operators on hover */
        .hover-ghost-marker {
          animation: hover-ghost-pulse 1s ease-in-out infinite;
        }
        
        @keyframes hover-ghost-pulse {
          0%, 100% {
            opacity: 0.9;
            filter: drop-shadow(0 0 4px rgba(245, 158, 11, 0.6));
          }
          50% {
            opacity: 1;
            filter: drop-shadow(0 0 12px rgba(245, 158, 11, 0.9));
          }
        }
        
        /* Smooth transition for markers during drawing mode */
        .leaflet-marker-pane .supercluster-marker,
        .leaflet-overlay-pane path {
          transition: opacity 0.2s ease, fill-opacity 0.2s ease;
        }
        
        /* Powered by Bestime */
        .powered-by-bestime {
          position: absolute;
          right: 12px;
          bottom: 12px;
          z-index: 500;
          font-size: 10px;
          color: hsl(var(--muted-foreground));
          font-family: "Instrument Sans", system-ui, sans-serif;
          pointer-events: none;
        }
        
        .powered-by-text {
          font-weight: 400;
        }
        
        .bestime-text {
          font-weight: 700;
        }
        
        /* Cache Status Indicator */
        .cache-indicator {
          position: absolute;
          right: 12px;
          bottom: 56px;
          z-index: 500;
        }
        
        .cache-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          background: hsl(var(--card));
          border: 1px solid hsl(var(--border));
          border-radius: 10px;
          color: hsl(var(--muted-foreground));
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }
        
        .cache-btn:hover {
          background: hsl(var(--secondary));
          color: hsl(var(--foreground));
        }
        
        .cache-btn.active {
          color: hsl(var(--primary));
          border-color: hsl(var(--primary) / 0.3);
        }
        
        .cache-btn .cache-dot {
          position: absolute;
          top: 6px;
          right: 6px;
          width: 8px;
          height: 8px;
          background: #22c55e;
          border-radius: 50%;
          border: 2px solid hsl(var(--card));
          animation: pulse 2s ease-in-out infinite;
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
        
        .cache-popup {
          position: absolute;
          bottom: 44px;
          right: 0;
          min-width: 200px;
          background: hsl(var(--card));
          border: 1px solid hsl(var(--border));
          border-radius: 12px;
          padding: 12px;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
          animation: slideUp 0.2s ease;
        }
        
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .cache-popup-title {
          font-size: 12px;
          font-weight: 600;
          color: hsl(var(--foreground));
          margin-bottom: 8px;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        
        .cache-popup-stats {
          display: flex;
          flex-direction: column;
          gap: 4px;
          margin-bottom: 10px;
        }
        
        .cache-stat {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 11px;
        }
        
        .cache-stat-label {
          color: hsl(var(--muted-foreground));
        }
        
        .cache-stat-value {
          font-weight: 600;
          color: hsl(var(--foreground));
          font-family: 'JetBrains Mono', monospace;
        }
        
        .cache-clear-btn {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 8px;
          background: hsl(var(--destructive) / 0.1);
          border: 1px solid hsl(var(--destructive) / 0.2);
          border-radius: 8px;
          color: hsl(var(--destructive));
          font-size: 11px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s ease;
        }
        
        .cache-clear-btn:hover {
          background: hsl(var(--destructive) / 0.15);
          border-color: hsl(var(--destructive) / 0.3);
        }
        
      `}</style>
      {/* Dynamic crosshair cursor during drawing mode */}
      {isDrawingMode && (
        <style>{`
          .leaflet-container,
          .leaflet-container .leaflet-interactive,
          .leaflet-container .leaflet-marker-pane,
          .leaflet-container .leaflet-overlay-pane {
            cursor: crosshair !important;
          }
        `}</style>
      )}
      <div
        ref={mapContainer}
        className={cn("w-full h-full", className)}
        style={{ minHeight: "400px", background: "hsl(var(--background))" }}
      />

      {/* Loading bar — shown while operators are loading */}
      {isLoading && (
        <div className="absolute top-0 left-0 right-0 z-[600] pointer-events-none">
          <div className="h-1 w-full bg-secondary/50 overflow-hidden">
            <div
              className="h-full bg-[#284899] rounded-r"
              style={{
                animation: "loading-bar 1.5s ease-in-out infinite",
                width: "40%",
              }}
            />
          </div>
          <style>{`
            @keyframes loading-bar {
              0% { transform: translateX(-100%); }
              100% { transform: translateX(350%); }
            }
          `}</style>
        </div>
      )}



      {/* Custom Zoom Control */}
      <div className="zoom-control-container">
        <div className="zoom-level-display">
          <span className="zoom-level-value">{localZoom}</span>
        </div>

        <button
          onClick={handleZoomIn}
          disabled={localZoom >= maxZoom}
          className="zoom-btn"
          title="Zoom avanti (+)"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M7 1V13M1 7H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        <div className="zoom-slider-track">
          <input
            type="range"
            min={minZoom}
            max={maxZoom}
            step={1}
            value={localZoom}
            onChange={(e) => handleZoomChange(Number(e.target.value))}
            className="zoom-slider"
            title={`Zoom: ${localZoom}`}
          />
          <div className="zoom-ticks">
            {Array.from({ length: maxZoom - minZoom + 1 }, (_, i) => (
              <div
                key={i}
                className={cn(
                  "zoom-tick",
                  (minZoom + i) === localZoom && "zoom-tick-active"
                )}
              />
            ))}
          </div>
        </div>

        <button
          onClick={handleZoomOut}
          disabled={localZoom <= minZoom}
          className="zoom-btn"
          title="Zoom indietro (-)"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M1 7H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        {/* Reset to Italy View Button */}
        <button
          onClick={handleResetToItaly}
          className="zoom-btn mt-2"
          title="Vista Italia"
        >
          <Globe className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Powered by Bestime */}
      <div className="powered-by-bestime">
        <span className="powered-by-text">Powered by </span>
        <span className="bestime-text">Bestime®️</span>
      </div>

      {/* Cache Status Indicator - Solo in dev mode */}
      {import.meta.env.DEV && (
        <div className="cache-indicator">
          <button
            className={cn("cache-btn", (isTileCacheRegistered || (operatorsCacheStats && operatorsCacheStats.totalOperators > 0)) && "active")}
            onClick={() => {
              setIsCacheInfoVisible(!isCacheInfoVisible);
              if (!isCacheInfoVisible) {
                refreshTileStats();
                refreshOperatorsStats();
              }
            }}
            title="Gestione cache dati"
          >
            <Database size={16} />
            {(isTileCacheRegistered || (operatorsCacheStats && operatorsCacheStats.totalOperators > 0)) && <span className="cache-dot" />}
          </button>

          {isCacheInfoVisible && (
            <div className="cache-popup" style={{ minWidth: '240px' }}>
              {/* Tiles Cache Section */}
              <div className="cache-section">
                <div className="cache-popup-title">
                  <Database size={14} />
                  Cache Tiles Mappa
                </div>

                <div className="cache-popup-stats">
                  <div className="cache-stat">
                    <span className="cache-stat-label">Stato</span>
                    <span className="cache-stat-value" style={{ color: isTileCacheRegistered ? '#22c55e' : '#ef4444' }}>
                      {isTileCacheRegistered ? 'Attivo' : 'Non attivo'}
                    </span>
                  </div>
                  {tileCacheStats && (
                    <>
                      <div className="cache-stat">
                        <span className="cache-stat-label">Tiles salvati</span>
                        <span className="cache-stat-value">{tileCacheStats.tileCount.toLocaleString()}</span>
                      </div>
                      <div className="cache-stat">
                        <span className="cache-stat-label">Dim. stimata</span>
                        <span className="cache-stat-value">
                          {tileCacheStats.estimatedSize > 1024 * 1024
                            ? `${(tileCacheStats.estimatedSize / (1024 * 1024)).toFixed(1)} MB`
                            : `${Math.round(tileCacheStats.estimatedSize / 1024)} KB`}
                        </span>
                      </div>
                    </>
                  )}
                </div>

                {isTileCacheRegistered && tileCacheStats && tileCacheStats.tileCount > 0 && (
                  <button
                    className="cache-clear-btn"
                    onClick={async () => {
                      await clearTileCache();
                      refreshTileStats();
                    }}
                  >
                    <Trash2 size={12} />
                    Svuota tiles
                  </button>
                )}
              </div>

              {/* Operators Cache Section */}
              <div className="cache-section" style={{ borderTop: '1px solid hsl(var(--border))', marginTop: '10px', paddingTop: '10px' }}>
                <div className="cache-popup-title">
                  <MapPin size={14} />
                  Cache Operatori (Pin)
                </div>

                <div className="cache-popup-stats">
                  <div className="cache-stat">
                    <span className="cache-stat-label">Operatori salvati</span>
                    <span className="cache-stat-value">
                      {operatorsCacheStats?.totalOperators.toLocaleString() || '0'}
                    </span>
                  </div>
                  <div className="cache-stat">
                    <span className="cache-stat-label">Set di filtri</span>
                    <span className="cache-stat-value">
                      {operatorsCacheStats?.totalEntries || '0'}
                    </span>
                  </div>
                  {operatorsCacheStats?.oldestEntry && (
                    <div className="cache-stat">
                      <span className="cache-stat-label">Ultimo aggiorn.</span>
                      <span className="cache-stat-value" style={{ fontSize: '10px' }}>
                        {operatorsCacheStats.oldestEntry.toLocaleDateString('it-IT', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  )}
                </div>

                {operatorsCacheStats && operatorsCacheStats.totalOperators > 0 && (
                  <button
                    className="cache-clear-btn"
                    disabled={isClearingOperators}
                    onClick={async () => {
                      await clearOperatorsCache();
                      refreshOperatorsStats();
                    }}
                  >
                    <Trash2 size={12} />
                    {isClearingOperators ? 'Svuotamento...' : 'Svuota operatori'}
                  </button>
                )}
              </div>

              {/* Clear All Button */}
              {((tileCacheStats && tileCacheStats.tileCount > 0) || (operatorsCacheStats && operatorsCacheStats.totalOperators > 0)) && (
                <button
                  className="cache-clear-btn"
                  style={{ marginTop: '10px', background: 'hsl(var(--destructive) / 0.15)' }}
                  disabled={isClearingOperators}
                  onClick={async () => {
                    await Promise.all([clearTileCache(), clearOperatorsCache()]);
                    refreshTileStats();
                    refreshOperatorsStats();
                  }}
                >
                  <Trash2 size={12} />
                  Svuota tutto
                </button>
              )}
            </div>
          )}
        </div>
      )}

    </div>
  );
}

export default MapView;
